import React, { Dispatch, useEffect, useRef, useState } from 'react';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import DialogFrame from '../_parts/DialogFrame';

import { useParams } from 'react-router-dom';

import { DialogContent, Typography } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';

import { onDynamicActionTaskReply } from '../../../../redux/project/documents/sliceProjectDocumentsDynamicSelection';
import AdditionalInputContent, { IAdditionalInputTypeEnum } from '../_parts/AdditionalInputContent';
import AdditionalInputFooterActions, {
    IAdditionalInfoResultButtonEnum,
} from '../_parts/AdditionalInputFooterActions';
import { useTranslation } from 'react-i18next';
import { vars } from '../../../../Theme';

const TIMEOUT_SECONDS = 180;

const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export default () => {
    const dispatch: Dispatch<unknown> = useDispatch();
    const { replicaId } = useParams();
    const { t } = useTranslation('translations', { keyPrefix: 'workflowAdditionalInput' });
    const { loading, additionalInput, error } = useSelector((_store: IAppReduxStore) => _store.projectDocumentsDynamicSelection);

    const [additionalInputState, setAdditionalInputState] = useState<string[]>([]);
    const [additionalInputErrorState, setAdditionalInputErrorState] = useState<boolean>(false);
    const [remainingSeconds, setRemainingSeconds] = useState<number>(TIMEOUT_SECONDS);
    const [timerStarted, setTimerStarted] = useState<boolean>(false);
    const intervalRef = useRef<NodeJS.Timer | null>(null);

    const onAdditionalInputStateChanged = (value: string[]) => {
        setAdditionalInputErrorState(false);
        setAdditionalInputState(value);
    };

    useEffect(() => {
        error && resetTimer();
    }, [error]);

    const resetTimer = () => {
        setRemainingSeconds(TIMEOUT_SECONDS);
        setTimerStarted(false);
        clearInterval(intervalRef.current);
    };

    useEffect(() => {
        if (additionalInput?.response) {
            setAdditionalInputState(additionalInput.response.dialogOptionDefault);
        }
    }, [additionalInput]);

    const onCloseDialog = () => {
        resetTimer();
        dispatch(
            onDynamicActionTaskReply({
                dialogResultButton: IAdditionalInfoResultButtonEnum.Cancel,
                additionalInputState: [],
                replicaId,
            }),
        );
    };

    useEffect(() => {
        if (timerStarted) {
            setRemainingSeconds(TIMEOUT_SECONDS);
            intervalRef.current = setInterval(() => {
                setRemainingSeconds(prev => {
                    if (prev <= 1) {
                        clearInterval(intervalRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
    }, [timerStarted]);

    useEffect(() => {
        remainingSeconds === 0 && onCloseDialog();
    }, [remainingSeconds]);

    const onConfirmClick = () => {
        setTimerStarted(true);
    };

    const timerColor = timerStarted && remainingSeconds <= 30 ? vars.errorDialogRed : vars.warningYellow;

    return (
        <DialogFrame open>
            <DialogHeader
                onClose={onCloseDialog}
                dialogTitle={additionalInput.response.dialogTitle}
            />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                {timerStarted && (
                    <Typography
                        sx={{
                            color: timerColor,
                            fontWeight: 700,
                            fontSize: '13px',
                            mb: 1,
                            textAlign: 'right',
                        }}
                    >
                        {t('dialogTimeout', { timeLeft: formatTime(remainingSeconds) })}
                    </Typography>
                )}
                <ContentLayout
                    loading={loading}
                    main={
                        <AdditionalInputContent
                            additionalInputState={additionalInputState}
                            onAdditionalInputStateChanged={onAdditionalInputStateChanged}
                            hasAdditionalInputError={additionalInputErrorState}
                            setAdditionalInputErrorState={setAdditionalInputErrorState}
                            type={IAdditionalInputTypeEnum.Generic}
                        />
                    }
                />
            </DialogContent>

            <AdditionalInputFooterActions
                additionalInputState={additionalInputState}
                setAdditionalInputErrorState={setAdditionalInputErrorState}
                type={IAdditionalInputTypeEnum.Generic}
                onConfirmClick={onConfirmClick}
            />
        </DialogFrame>
    );
};
